import { HTMLAttributes, useMemo, useRef, useState } from 'react'
import { Transition } from 'react-transition-group'
import { useGSAP } from '@gsap/react'
import gsap from 'gsap'
import { SplitText } from 'gsap/dist/SplitText'
import { map } from 'lodash'
import Image from 'next/image'
import Link from 'next/link'

import Typography from '@/components/base/typography'
import { useLoaderStore } from '@/store/loader'

import { cn } from '@/lib/utils'
import { WPImage } from '@/types'

export type NewsCarouselCard = {
  title: {
    rendered: string
  }
  featured_image: WPImage
  link: string
}

export type NewsCarousel = {
  collection: NewsCarouselCard[]
}

export type NewsCarouselCardProps = Omit<HTMLAttributes<HTMLDivElement>, 'title'> &
  NewsCarouselCard & {
    active: boolean
    onChange?: () => void
  }

const NewsCarouselCard = ({
  active,
  title,
  link,
  featured_image,
  onChange,
  className
}: NewsCarouselCardProps) => {
  const root = useRef<HTMLAnchorElement>(null!)
  const circle = useRef<SVGCircleElement>(null!)
  const length = useMemo(() => 185.0545196533203, [])
  const progress = useMemo(() => ({ value: 0 }), [])
  const splitTitle = useRef<SplitText>(null)
  const timeline = useRef<GSAPTimeline>(null)
  const fontsReady = useLoaderStore((state) => state.fontsReady)

  const { context, contextSafe } = useGSAP({ scope: root })

  const makeTimeline = contextSafe((done?: () => void) => {
    if (!fontsReady) {
      done?.()
      return
    }

    const title = context.selector?.('[data-motion-title]')[0]
    const image = context.selector?.('[data-motion-image]')[0]
    const mask = context.selector?.('[data-motion-image-mask]')[0]

    active && (splitTitle.current = SplitText.create(title, { type: 'lines', mask: 'lines' }))

    timeline.current = gsap.timeline({ onComplete: done })

    if (active) {
      timeline.current.add(
        gsap
          .timeline({ defaults: { duration: 0.5, ease: 'power2.inOut' }, onComplete: onChange })
          .from(mask, { scale: 0 })
          .from(image, { scale: 2 }, 0)
          .from(splitTitle.current?.lines || [], { yPercent: 100, stagger: 0.05 }, 0)
          .fromTo(
            progress,
            { value: length },
            {
              value: 0,
              duration: 5,
              ease: 'none',
              onUpdate: () => {
                if (circle.current) {
                  circle.current.style.strokeDashoffset = progress.value.toString()
                }
              }
            },
            0
          ),
        0.5
      )
    } else {
      timeline.current.add(
        gsap
          .timeline({ defaults: { duration: 0.5, ease: 'power2.inOut' } })
          .to(progress, {
            value: -length,
            duration: 0.7,
            ease: 'power2.inOut',
            onUpdate: () => {
              if (circle.current) {
                circle.current.style.strokeDashoffset = progress.value.toString()
              }
            }
          })
          .to(mask, { scale: 0 }, 0.2)
          .to(image, { scale: 2 }, '<')
          .to(splitTitle.current?.lines || [], { yPercent: -100, stagger: 0.05 }, '<'),
        0
      )
    }
  })

  return (
    <Transition appear in={active} nodeRef={root} addEndListener={makeTimeline} unmountOnExit>
      <Link
        ref={root}
        href={link}
        className={cn(
          'flex items-center justify-start space-x-[2rem] rounded-[0.5rem] px-[2rem] py-[1rem]',
          'focus-ring-light',
          className
        )}
      >
        <span className="grid size-[6rem] shrink-0 grow-0">
          <svg
            viewBox="0 0 60 60"
            className="col-[1/-1] row-[1/-1] -rotate-90"
            xmlns="http://www.w3.org/2000/svg"
            version="1.1"
            fill="none"
          >
            <circle
              r="29.5"
              cx="30"
              cy="30"
              strokeWidth="1"
              strokeOpacity={0.2}
              strokeDashoffset={0}
              strokeDasharray={length}
              className="stroke-current"
            />
            <circle
              ref={circle}
              r="29.5"
              cx="30"
              cy="30"
              strokeWidth="1"
              strokeLinecap="round"
              strokeDashoffset={0}
              strokeDasharray={length}
              className="stroke-current"
            />
          </svg>
          <span className={cn('col-[1/-1] row-[1/-1]', 'flex items-center justify-center')}>
            <span
              className="relative flex size-[4.8rem] overflow-hidden rounded-full bg-white/20"
              data-motion-image-mask
            >
              <Image
                src={featured_image.url}
                alt=""
                fill
                loading="lazy"
                className="rounded-full object-cover"
                data-motion-image
              />
            </span>
          </span>
        </span>
        <span className="flex">
          <Typography variant="breadcrumbs" className="font-bold" data-motion-title>
            {title?.rendered}
          </Typography>
        </span>
      </Link>
    </Transition>
  )
}

export type NewsCarouselProps = HTMLAttributes<HTMLDivElement> & NewsCarousel

const NewsCarousel = ({ collection, className, ...props }: NewsCarouselProps) => {
  const [currentIndex, setCurrentIndex] = useState(0)
  const fontsReady = useLoaderStore((state) => state.fontsReady)

  if (!fontsReady) return null

  return (
    <div
      className={cn(
        'h-[10rem] w-[23rem] rounded-[0.5rem] bg-white/15 text-white backdrop-blur-sm',
        className
      )}
      {...props}
    >
      {map(collection, (card, index) => (
        <NewsCarouselCard
          key={`card-${index}`}
          active={index === currentIndex}
          className={cn('absolute left-0 top-0 size-full')}
          onChange={() => {
            setCurrentIndex((currentIndex) =>
              currentIndex === collection.length - 1 ? 0 : currentIndex + 1
            )
          }}
          {...card}
        />
      ))}
    </div>
  )
}

export default NewsCarousel
